import { useEffect, useState } from 'react';
import { HiOutlineChevronLeft } from 'react-icons/hi';
import ProductForm from './components/ProductForm';
import ProductInfo from './components/ProductInfo';
import ProductImage from './components/ProductImage';
import ProductTitleBlock from './components/ProductTitleBlock';
import { createGuid } from '../../../utils/createGuid';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  addToCart,
  refreshBasket,
  removeItem,
  setBasketStore,
  updateItem,
} from '../../../store/basket/basketSlice';
import toast from '../../ui/toast/toast';
import Notification from '../../ui/Notification';
import getPrices from '../../../utils/getPrices';
import ConfirmDialog from '../../shared/ConfirmDialog';
import useCouponValidation from '../../../utils/hooks/useCouponValidation';
import calculateBXGYQuantity from '../../../utils/coupon/calculateBXGYQuantity';

const ProductDetail = ({
  product,
  isModal = false,
  onClose,
  lockedItemId,
  onAddFreeItem,
  coupon,
  onAddBXGYItem,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUpsell = searchParams.get('fromupsell');
  const basketProduct = product?.basketProduct || null;
  const basket = useSelector((state) => state.basket.basket);
  const store = useSelector((state) => state.base.common.currentStore);
  const [productNote, setProductNote] = useState(
    basketProduct ? basketProduct.note : ''
  );
  const [headerKcal, setHeaderKcal] = useState(null);
  const [newStoreProduct, setNewStoreProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const { appliedCoupon } = useCouponValidation();

  useEffect(() => {
    if (!isModal) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth', // Yumuşak kaydırma efekti
      });
    }
  }, [isModal]);

  const handleAddToCart = (values, addConfirm) => {
    // If this is a BXGY item (onAddBXGYItem callback exists), use it
    if (onAddBXGYItem && coupon?.type === 2) {
      const { qty } = values; // qty is total quantity user wants (paid + free)
      const { foundItem, refinedOptionList } = getPrices(values, product);
      // Calculate BXGY quantities using new logic (only 1 free)
      const bxgyResult = calculateBXGYQuantity(coupon, qty);
      const { paidQuantity, freeQuantity, totalQuantity } = bxgyResult;

      onAddBXGYItem({
        item: foundItem,
        quantity: qty, // Use user's selected qty, not calculated totalQuantity
        paidQuantity,
        freeQuantity,
        options: refinedOptionList,
      });
      if (isModal && onClose) {
        onClose();
      }
      return;
    }

    // If this is a free item (onAddFreeItem callback exists), use it
    if (onAddFreeItem) {
      const { qty } = values;
      const { foundItem, refinedOptionList } = getPrices(values, product);
      onAddFreeItem({
        item: foundItem,
        quantity: qty,
        options: refinedOptionList,
      });
      if (isModal && onClose) {
        onClose();
      }
      return;
    }

    // Check store diff — the basket can only hold items from one store at
    // a time. If the user tries to add from a different store (or the
    // basketStore reference got out of sync with the items), force a
    // confirm dialog before dropping the existing cart.
    const hasItems = basket.items.length > 0;
    const basketSlug = basket.basketStore?.storeSlug || null;
    const currentSlug = params?.storeSlug || null;
    const sameStore = basketSlug === currentSlug && basketSlug !== null;

    if (!hasItems || addConfirm) {
      dispatch(
        setBasketStore({
          storeSlug: currentSlug,
          name: store?.name,
          storelogo: store?.storelogo,
          address: store?.address,
          apartment: store?.apartment,
          city: store?.city,
          state: store?.state,
          postalcode: store?.postalcode,
        })
      );
    } else if (!sameStore) {
      setNewStoreProduct({
        store: currentSlug,
        values: values,
      });
      return;
    }

    //End check store diff
    const { qty } = values;
    const { total, foundItem, refinedOptionList } = getPrices(values, product);
    const payload = {
      basketId: createGuid(),
      product: {
        ...product,
      },
      item: {
        name: foundItem.name,
        price: foundItem.price,
        fee: foundItem.itemfee,
        id: foundItem.id,
      },
      options: refinedOptionList,
      note: productNote,
      qty: qty,
      total: total,
    };
    dispatch(addToCart(payload));
    toast.push(
      <Notification
        title={'Added to your cart!'}
        type="success"
        duration={2000}
      />,
      {
        placement: 'top-center',
      }
    );

    // Close modal if in modal mode
    if (isModal && onClose) {
      onClose();
      return;
    }

    // If the product is added from upsell, go back to the previous page
    if (fromUpsell) {
      navigate(-1);
    } else {
      navigate(`/${params?.storeSlug}`);
    }
  };

  const handleUpdate = (values) => {
    const { qty } = values;
    const { total, foundItem, refinedOptionList } = getPrices(values, product);

    // Check if this is a BXGY item and calculate updated bxgyInfo
    let bxgyInfo = null;
    const isBXGYItem = basketProduct?.bxgyInfo || (coupon?.type === 2);
    if (isBXGYItem) {
      const activeCoupon = coupon || appliedCoupon;
      if (activeCoupon) {
        const bxgyResult = calculateBXGYQuantity(activeCoupon, qty);
        if (bxgyResult.canApply) {
          bxgyInfo = {
            paidQuantity: bxgyResult.paidQuantity,
            freeQuantity: bxgyResult.freeQuantity,
            totalQuantity: bxgyResult.totalQuantity,
          };
        }
      }
    }

    // Calculate new subtotal after update
    const newSubtotal = basket.items.reduce((sum, item) => {
      if (item.basketId === basketProduct.basketId) {
        return sum + total;
      }
      return sum + (item.total || 0);
    }, 0);


    const payload = {
      basketId: basketProduct.basketId,
      product: {
        ...product,
      },
      item: {
        name: foundItem.name,
        price: foundItem.price,
        fee: foundItem.itemfee,
        id: foundItem.id,
      },
      options: refinedOptionList,
      note: productNote,
      qty: qty,
      total: total,
      ...(bxgyInfo && { bxgyInfo }),
    };
    dispatch(updateItem(payload));
    toast.push(
      <Notification
        title={'Your selection has been updated!'}
        type="success"
        duration={2000}
      ></Notification>,
      {
        placement: 'top-center',
      }
    );

    if (isModal && onClose) {
      onClose();
      return;
    }

    navigate(-1);
  };

  const handleDelete = () => {
    dispatch(removeItem(basketProduct.basketId));
    toast.push(
      <Notification
        title={'Your selection has been deleted!'}
        type="success"
        duration={2000}
      ></Notification>,
      {
        placement: 'top-center',
      }
    );

    // Close modal if in modal mode, otherwise navigate back
    if (isModal && onClose) {
      onClose();
      return;
    }

    navigate(-1);
  };

  const handleNewStoreProductConfirm = () => {
    setLoading(true);
    dispatch(refreshBasket());
    setTimeout(() => {
      handleAddToCart(newStoreProduct.values, true);
    }, 1500);
  };

  /*   const checkStoreClose = () => {
    const isCurrentlyClose = store.websiteclose || !store.isAvailable;

    if (isCurrentlyClose) {
      toast.push(
        <Notification title="Store is closed." type="danger" duration={2000} />
      );
      return true;
    }

    return false;
  }; */

  return (
    <>
      <div>
        {!isModal && (
          <Link
            to={basketProduct ? -1 : `/${params?.storeSlug}`}
            className="mb-9 mt-5 flex items-center gap-1 -ml-1 w-fit"
          >
            <HiOutlineChevronLeft className="text-lg" />
            <h5 className="text-[15px] font-semibold">
              {basketProduct ? 'Back To Checkout' : 'Back to Menu'}
            </h5>
          </Link>
        )}
        {isModal ? (
          <div
            className="relative"
            style={{ fontFamily: 'Geist, system-ui, sans-serif' }}
          >
            <style>{`
              .pm-shell {
                display: flex;
                flex-direction: column;
                max-height: 92dvh;
                height: 92dvh;
              }
              .pm-top {
                flex-shrink: 0;
                padding: 26px 26px 14px;
                display: grid;
                grid-template-columns: 120px 1fr;
                gap: 16px;
                align-items: start;
              }
              .pm-form-host {
                flex: 1;
                min-height: 0;
                padding: 0 26px 22px;
                display: flex;
                flex-direction: column;
              }
              @media (min-width: 768px) {
                .pm-shell {
                  max-height: 86vh;
                  height: auto;
                  min-height: 520px;
                }
                .pm-top {
                  padding: 32px 32px 16px;
                  grid-template-columns: 260px 1fr;
                  gap: 28px;
                }
                .pm-form-host { padding: 0 32px 28px; }
              }
            `}</style>

            <div className="pm-shell relative">
              <div className="pm-top">
                <div>
                  <ProductImage product={product} />
                </div>
                <div>
                  <ProductTitleBlock
                    product={product}
                    coupon={coupon}
                    kcalLabel={headerKcal}
                  />
                </div>
              </div>

              <div className="pm-form-host">
                <ProductForm
                  isModal={isModal}
                  productNote={productNote}
                  onProductNoteChange={(note) => setProductNote(note)}
                  basketProduct={basketProduct}
                  product={product}
                  onSubmit={basketProduct ? handleUpdate : handleAddToCart}
                  onDelete={handleDelete}
                  lockedItemId={lockedItemId}
                  coupon={coupon}
                  onAddBXGYItem={onAddBXGYItem}
                  onKcalChange={setHeaderKcal}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-start gap-6 md:gap-10">
            <div className="sm:max-w-[200px] md:max-w-[250px] w-full sm:sticky sm:top-[143px]">
              <ProductInfo product={product} isModal={isModal} coupon={coupon} />
            </div>
            <div className="flex-1 w-full">
              <ProductForm
                productNote={productNote}
                onProductNoteChange={(note) => setProductNote(note)}
                basketProduct={basketProduct}
                product={product}
                onSubmit={basketProduct ? handleUpdate : handleAddToCart}
                onDelete={handleDelete}
                lockedItemId={lockedItemId}
                coupon={coupon}
                onAddBXGYItem={onAddBXGYItem}
              />
            </div>
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={newStoreProduct}
        type="danger"
        title="By adding this product your cart will be emptied. Should it still be added?"
        onClose={() => setNewStoreProduct(null)}
        onCancel={() => setNewStoreProduct(null)}
        onRequestClose={() => setNewStoreProduct(null)}
        onConfirm={() => handleNewStoreProductConfirm()}
        loading={loading}
      >
        <p>Your cart contains items from a different restaurant.</p>
      </ConfirmDialog>
    </>
  );
};

export default ProductDetail;
